import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { UnsavedChangesDialog } from "../components/UnsavedChangesDialog";
import { usePlaceNavigation } from "../hooks/usePlaceNavigation";
import { useUnsavedChangesWarning } from "../hooks/useUnsavedChangesWarning";
import { toast } from "~/components/ui/toaster";
import {
  type AdventureMap,
  checkSlugAvailability,
  createCuratorProfile,
  getLandingPage,
  getMapById,
  getGeoJsonPlacesByAdventureMapId,
  isCuratorProfileRequiredError,
  type LandingPage,
  type LandingPageConfig,
  type LandingPageLogo,
  type PlaceFeature,
  type PlaceGeoJSON,
  type PlacePreviewFeature,
  PLACE_TYPE_META,
  type PlaceType,
  updateLandingPage,
  uploadLandingPageLogo,
} from "../lib/api";
import { emptyPlacesGeoJSON } from "../lib/mapConstants";
import { sortPlaceTypes } from "../lib/placeTypes";
import { routes } from "../lib/routes";
import { slugify } from "../lib/slug";
import { useInstrumentation } from "../hooks/useInstrumentation";
import { BreakpointProvider } from "../hooks/useBreakpoint";
import { cn } from "../lib/utils";
import { CuratorProfileDialog } from "../components/CuratorProfileDialog";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Icon } from "../components/ui/icons/icon";
import { ImageDropzone } from "../components/ui/image-dropzone";
import { ImageThumbnail } from "../components/ui/image-thumbnail";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useImageUpload } from "../hooks/useImageUpload";
import { useShare } from "../hooks/useShare";
import MobileLandingPage from "./MobileLandingPage";
import DesktopLandingPage from "./DesktopLandingPage";

type PreviewMode = "desktop" | "mobile";

export default function LandingPageBuilder() {
  const { id: mapId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const instrumentation = useInstrumentation();

  const [mapData, setMapData] = useState<AdventureMap | null>(null);
  const [landingPageData, setLandingPageData] = useState<LandingPage | null>(null);
  const [places, setPlaces] = useState<PlaceGeoJSON>(emptyPlacesGeoJSON);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [config, setConfig] = useState<LandingPageConfig>({});
  const [slug, setSlug] = useState("");
  const [slugStatus, setSlugStatus] = useState<"idle" | "checking" | "available" | "taken">("idle");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState<PreviewMode>("desktop");
  const [showPreview, setShowPreview] = useState(false);
  const [curatorDialogOpen, setCuratorDialogOpen] = useState(false);
  const [savingCurator, setSavingCurator] = useState(false);
  const [previewSelectedPlace, setPreviewSelectedPlace] = useState<PlaceFeature | null>(null);
  const [logo, setLogo] = useState<LandingPageLogo | null>(null);

  const share = useShare();
  const logoInputRef = useRef<HTMLInputElement>(null);

  const handlePlaceChange = useCallback(
    (data: string | PlacePreviewFeature | null) => {
      if (!data) {
        setPreviewSelectedPlace(null);
      } else if (typeof data === "string") {
        setPreviewSelectedPlace(places.features.find((f) => f.id === data) ?? null);
      } else {
        setPreviewSelectedPlace(data as PlaceFeature);
      }
    },
    [places.features],
  );

  const { clearPlaceSelection } = usePlaceNavigation({
    selectedPlaceId: previewSelectedPlace?.properties.id ?? null,
    onPlaceChange: handlePlaceChange,
  });

  // Place types that have at least one place
  const activeTypes = useMemo(() => {
    const types = new Set<PlaceType>();
    places.features.forEach((f) => types.add(f.properties.type));
    return sortPlaceTypes(Array.from(types));
  }, [places.features]);

  // Map data with current (unsaved) values for preview
  const previewMapData = useMemo(
    () =>
      mapData && {
        name,
        description,
        slug: slug || landingPageData?.slug || "",
        config,
        logo,
        curatedBy: mapData.curatedBy,
      },
    [mapData, landingPageData?.slug, name, description, slug, config, logo],
  );

  // Load map, places, and landing page data
  useEffect(() => {
    if (!mapId) return;

    setLoading(true);
    Promise.all([getMapById(mapId), getGeoJsonPlacesByAdventureMapId(mapId), getLandingPage(mapId)])
      .then(([map, placesData, landingData]) => {
        setMapData(map);
        setLandingPageData(landingData);
        setPlaces(placesData);
        setName(map.name);
        setDescription(map.description || "");
        setConfig(landingData?.config || {});
        setSlug(landingData?.slug || slugify(map.name));
        setLogo(landingData?.logo || null);

        instrumentation.track("Landing Page Builder Opened", {
          mapId: map.id,
          isAlreadyPublished: !!landingData?.publishedAt,
        });
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load map");
        navigate(routes.to.home(), { replace: true });
      })
      .finally(() => setLoading(false));
  }, [mapId, navigate, instrumentation]);

  // Update section description
  const handleDescriptionChange = (type: PlaceType, description: string) => {
    setConfig((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [type]: {
          ...prev.sections?.[type],
          description: description || undefined,
        },
      },
    }));
  };

  const handleLogoUpload = useCallback(
    async (file: Blob) => {
      if (!mapId) throw new Error("mapId required for upload");
      return uploadLandingPageLogo(mapId, file as File);
    },
    [mapId],
  );

  const { upload: uploadLogo, uploading: uploadingLogo } = useImageUpload({
    uploadFn: handleLogoUpload,
    onSuccess: setLogo,
  });

  const handleRemoveLogo = () => setLogo(null);

  const hasUnsavedChanges = useMemo(() => {
    if (!mapData) return false;
    const nameChanged = name !== mapData.name;
    const descriptionChanged = description !== (mapData.description || "");
    const slugChanged = slug !== (landingPageData?.slug || "");
    const configChanged = JSON.stringify(config) !== JSON.stringify(landingPageData?.config || {});
    const logoChanged = logo?.id !== landingPageData?.logo?.id;
    return nameChanged || descriptionChanged || slugChanged || configChanged || logoChanged;
  }, [mapData, landingPageData, name, description, slug, config, logo]);

  const blocker = useUnsavedChangesWarning(hasUnsavedChanges);

  // Validation
  const isSlugChanged = slug !== (landingPageData?.slug || "");
  const isSlugValid = slugStatus === "available" || (slugStatus === "idle" && !isSlugChanged);
  const isNameValid = name.trim().length > 0;
  const isPublished = !!landingPageData?.publishedAt;
  const canSave = isNameValid && isSlugValid && (isPublished ? hasUnsavedChanges : true);

  // Debounced slug availability check
  useEffect(() => {
    if (!mapId || !slug || !isSlugChanged) {
      setSlugStatus("idle");
      return;
    }

    setSlugStatus("checking");
    const timeout = setTimeout(async () => {
      try {
        const result = await checkSlugAvailability(mapId, slug);
        setSlugStatus(result.available ? "available" : "taken");
      } catch {
        setSlugStatus("idle");
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [mapId, slug, isSlugChanged]);

  const handleSlugChange = (value: string) => {
    setSlug(slugify(value, { trim: false }));
  };

  // Save and publish
  const handleSave = async () => {
    if (!mapId || !mapData) return;
    if (slugStatus === "taken") {
      toast.error("Please choose a different URL slug");
      return;
    }

    setSaving(true);
    try {
      const logoChanged = logo?.id !== landingPageData?.logo?.id;
      const logoId = logoChanged ? (logo?.id ?? "") : undefined;

      const result = await updateLandingPage(mapId, {
        name,
        description,
        config,
        slug,
        publish: true,
        logoId,
      });

      setLandingPageData(result);
      setConfig(result.config || {});
      setSlug(result.slug || "");
      setLogo(result.logo);
      setMapData({
        ...mapData,
        name,
        description,
      });
      toast.success(isPublished ? "Changes saved" : "Landing page published!");
    } catch (err) {
      if (isCuratorProfileRequiredError(err)) {
        setCuratorDialogOpen(true);
      } else {
        console.error(err);
        toast.error("Failed to save");
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle curator profile creation then retry save
  const handleCuratorCreate = async (data: { displayName: string }) => {
    setSavingCurator(true);
    try {
      await createCuratorProfile({ ...data, trigger: "publish_landing_page" });
      setCuratorDialogOpen(false);
      // Retry the save
      await handleSave();
    } catch {
      toast.error("Failed to create profile");
    } finally {
      setSavingCurator(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center p-4">
        <Card className="p-8 text-center text-muted-foreground">Loading...</Card>
      </div>
    );
  }

  if (!mapData) {
    return (
      <div className="min-h-full flex items-center justify-center p-4">
        <Card className="p-8 text-center">
          <p className="text-destructive mb-4">Map not found</p>
          <Button asChild variant="secondary">
            <Link to={routes.to.home()}>Go home</Link>
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full grid grid-cols-1 xl:grid-cols-[1fr_2fr]">
      {/* Editor column */}
      <div
        className={cn(
          "h-full flex flex-col bg-muted/30 xl:border-r xl:border-border overflow-hidden",
          showPreview ? "hidden xl:flex" : "flex",
        )}
      >
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background border-b border-border shrink-0">
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button asChild variant="ghost" size="icon">
                <Link to={routes.to.maps.view(mapId!)}>
                  <Icon i="back" className="size-5" />
                </Link>
              </Button>
              <div>
                <h1 className="font-semibold text-foreground">{mapData.name}</h1>
                <p className="text-sm text-muted-foreground">Landing Page Builder</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowPreview(true)} className="xl:hidden">
                Preview
              </Button>
              <Button onClick={handleSave} disabled={saving || !canSave}>
                {saving ? "Saving..." : isPublished ? "Save changes" : "Publish"}
              </Button>
            </div>
          </div>
        </header>

        {/* Editor content */}
        <main className="flex-1 overflow-auto px-4 py-8">
          <div className="max-w-xl mx-auto">
            {/* Your Page - URL and branding */}
            <Card className="p-6 mb-6">
              <h2 className="flex items-center gap-2 text-lg font-semibold mb-1">
                <Icon i="link" className="size-5" />
                Your Page
              </h2>
              <p className="text-sm text-muted-foreground mb-4">Customize your page&apos;s URL and branding.</p>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="page-slug" className="text-base">
                    URL
                  </Label>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="relative flex-1">
                      <Input
                        id="page-slug"
                        value={slug}
                        onChange={(e) => handleSlugChange(e.target.value)}
                        placeholder="my-adventure"
                        className={cn(
                          "pr-24",
                          slugStatus === "taken" && "border-destructive focus-visible:ring-destructive",
                          slugStatus === "available" && "border-green-500 focus-visible:ring-green-500",
                        )}
                      />
                      <span className="absolute right-8 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        .noma.to
                      </span>
                      {slugStatus === "checking" && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">
                          ...
                        </span>
                      )}
                      {slugStatus === "available" && (
                        <Icon i="check" className="absolute right-2 top-1/2 -translate-y-1/2 size-4 text-green-500" />
                      )}
                      {slugStatus === "taken" && (
                        <Icon i="close" className="absolute right-2 top-1/2 -translate-y-1/2 size-4 text-destructive" />
                      )}
                    </div>
                    {isPublished && landingPageData?.slug && (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="shrink-0"
                        onClick={() =>
                          share({
                            url: routes.to.landingPages.bySlug(landingPageData.slug!),
                            title: name,
                          })
                        }
                      >
                        <Icon i="share" className="size-4" />
                        Share
                      </Button>
                    )}
                  </div>
                  {slugStatus === "taken" && (
                    <p className="text-sm text-destructive mt-2">This URL is already taken or invalid.</p>
                  )}
                </div>

                <div>
                  <Label className="text-base">Logo</Label>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadLogo(file);
                      e.target.value = "";
                    }}
                  />
                  {logo ? (
                    <div className="flex items-center gap-3 mt-2">
                      <ImageThumbnail
                        src={logo.thumb}
                        alt="Logo"
                        onDelete={handleRemoveLogo}
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="h-16 w-auto border rounded-xs object-contain"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="shadow-none"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                      >
                        Replace
                      </Button>
                    </div>
                  ) : (
                    <ImageDropzone
                      onFilesSelect={(files) => files[0] && uploadLogo(files[0])}
                      disabled={uploadingLogo}
                      className="mt-2"
                    />
                  )}
                </div>
              </div>
            </Card>

            {/* Map Details - title and description */}
            <Card className="p-6 mb-6">
              <h2 className="flex items-center gap-2 text-lg font-semibold mb-1">
                <Icon i="map-outline" className="size-5" />
                Map Details
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                The title and description shown on your landing page.
              </p>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="map-name" className="text-base">
                    Title
                  </Label>
                  <Input
                    id="map-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="My Adventure Map"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="map-description" className="text-base">
                    Description
                  </Label>
                  <Textarea
                    id="map-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="A brief description of your adventure..."
                    className="mt-2"
                    rows={3}
                  />
                </div>
              </div>
            </Card>

            {/* Section descriptions */}
            <Card className="p-6">
              <h2 className="flex items-center gap-2 text-lg font-semibold mb-1">
                <Icon i="text" className="size-5" />
                Section Descriptions
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                Add optional descriptions to introduce each section on your landing page.
              </p>

              {activeTypes.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Add some places to your map first to customize section descriptions.
                </p>
              ) : (
                <div className="space-y-6">
                  {activeTypes.map((type) => (
                    <div key={type}>
                      <Label htmlFor={`section-${type}`} className="text-base">
                        {PLACE_TYPE_META[type].labelPlural}
                      </Label>
                      <Textarea
                        id={`section-${type}`}
                        placeholder={`Describe your ${PLACE_TYPE_META[type].labelPlural.toLowerCase()}...`}
                        value={config.sections?.[type]?.description || ""}
                        onChange={(e) => handleDescriptionChange(type, e.target.value)}
                        className="mt-2"
                        rows={3}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </main>
      </div>

      {/* Preview column */}
      <div className={cn("h-full flex flex-col overflow-hidden relative", showPreview ? "flex" : "hidden xl:flex")}>
        {/* Floating preview mode toggle (XL only) */}
        <div className="hidden xl:flex absolute top-6 left-1/2 -translate-x-1/2 z-floating-panel rounded-xl border border-border bg-background/95 backdrop-blur p-1.5 shadow-lg">
          <button
            onClick={() => setPreviewMode("desktop")}
            className={cn(
              "px-5 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer",
              previewMode === "desktop" ? "bg-primary text-primary-foreground" : "hover:bg-muted",
            )}
          >
            Desktop
          </button>
          <button
            onClick={() => setPreviewMode("mobile")}
            className={cn(
              "px-5 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer",
              previewMode === "mobile" ? "bg-primary text-primary-foreground" : "hover:bg-muted",
            )}
          >
            Mobile
          </button>
        </div>

        {/* Preview toolbar (< XL only) */}
        <div className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border xl:hidden shrink-0">
          <div className="px-4 py-3 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}>
              <Icon i="back" className="size-4 sm:mr-2" />
              <span className="hidden sm:inline">Back to editor</span>
            </Button>

            <div className="flex rounded-lg border border-border p-1">
              <button
                onClick={() => setPreviewMode("desktop")}
                className={cn(
                  "px-3 py-1 text-sm rounded-md transition-colors cursor-pointer",
                  previewMode === "desktop" ? "bg-primary text-primary-foreground" : "hover:bg-muted",
                )}
              >
                Desktop
              </button>
              <button
                onClick={() => setPreviewMode("mobile")}
                className={cn(
                  "px-3 py-1 text-sm rounded-md transition-colors cursor-pointer",
                  previewMode === "mobile" ? "bg-primary text-primary-foreground" : "hover:bg-muted",
                )}
              >
                Mobile
              </button>
            </div>

            <Button onClick={handleSave} disabled={saving || !canSave}>
              {saving ? "Saving..." : isPublished ? "Save changes" : "Publish"}
            </Button>
          </div>
        </div>

        {/* Preview content */}
        {previewMapData && previewMode === "mobile" && (
          <div className="flex-1 min-h-0 flex items-center justify-center">
            <div className="w-full max-w-[390px] h-full max-h-[844px] border border-border rounded-lg overflow-hidden bg-background transform-gpu">
              <BreakpointProvider breakpoint="sm">
                <MobileLandingPage
                  landingPage={previewMapData}
                  places={places}
                  selectedPlace={previewSelectedPlace}
                  onPlaceSelect={(id) => setPreviewSelectedPlace(places.features.find((f) => f.id === id) ?? null)}
                  onPlaceDeselect={clearPlaceSelection}
                />
              </BreakpointProvider>
            </div>
          </div>
        )}
        {previewMapData && previewMode === "desktop" && (
          <div className="flex-1 min-h-0 overflow-auto">
            <BreakpointProvider breakpoint="xl">
              <DesktopLandingPage
                landingPage={previewMapData}
                places={places}
                selectedPlace={previewSelectedPlace}
                onPlaceSelect={(id) => setPreviewSelectedPlace(places.features.find((f) => f.id === id) ?? null)}
                onPlaceDeselect={clearPlaceSelection}
              />
            </BreakpointProvider>
          </div>
        )}
      </div>

      <CuratorProfileDialog
        open={curatorDialogOpen}
        onOpenChange={setCuratorDialogOpen}
        saving={savingCurator}
        onSave={handleCuratorCreate}
      />
      <UnsavedChangesDialog blocker={blocker} />
    </div>
  );
}
